import { createFileRoute } from '@tanstack/react-router';
import * as v from 'valibot';
import { useTranslation } from '@/lib/i18n';
import { useDeferredValue, useState, useEffect, useMemo, useRef, Suspense, startTransition } from 'react';
import { isServer, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { currentWindowKey, type LiveCounterMode } from '@/lib/data';
import { trpc } from '@/lib/trpc';
import { toast } from '@/lib/toast';
import { AnimatedTotalRaised } from './-components/animated-total-raised';
import type { Locale } from '@worldcoo/monolith/core';
import { flagUrls, countryCodeToLocale } from '@/lib/country-flags';
import { Layout } from './-components/layout';
import { WorldcooLogo } from '@worldcoo/ui/custom/worldcoo-logo';
import { motion, AnimatePresence } from 'motion/react';
import { APP_VERSION_STORAGE_KEY } from '@/lib/app-version';

export const Route = createFileRoute('/www/live-counter')({
	validateSearch: v.object({
		mode: v.fallback(v.picklist(['day', 'realtime']), 'realtime'),
	}),
	head: () => ({
		meta: [{ name: 'robots', content: 'noindex, nofollow' }],
	}),
	component: Component,
	pendingComponent: LiveCounterPending,
});

// This page has none of the site chrome (no header, no footer, it is a full screen counter meant to
// be left open on a screen), so it gets its own pending UI instead of the www one, and reuses the
// same "..." placeholder its counters already fall back to.
const PAGE_CLASS = 'flex min-h-dvh flex-col items-center justify-start';
const COUNTERS_STACK_CLASS =
	'flex w-full flex-col items-center gap-[clamp(1.25rem,3vh,2.5rem)] px-6 pt-[clamp(1.25rem,3.5vh,3rem)]';

function LiveCounterPending() {
	return (
		<Layout className={PAGE_CLASS}>
			<div className={COUNTERS_STACK_CLASS}>
				<CounterFallback />
			</div>
		</Layout>
	);
}

// ---------------------------------------------------------------------------
// Country flags (SVG icon components)
// ---------------------------------------------------------------------------

function getCountryFlagUrl(countryCode: string) {
	const locale = countryCodeToLocale[countryCode];
	return locale ? flagUrls[locale] : undefined;
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ApiDonation = {
	amountCents: number;
	timestamp: number;
	campaignName: string | null;
	merchantName: string | null;
	city: string | null;
	country: string | null;
};

type FeedDonation = ApiDonation & { id: string };

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

function Component() {
	const { mode } = Route.useSearch();
	const navigate = Route.useNavigate();
	const queryClient = useQueryClient();

	// Track the current 5-minute window key (same as landing page)
	const [windowKey, setWindowKey] = useState(currentWindowKey);
	useEffect(() => {
		const timer = setInterval(() => {
			const nextWindowKey = currentWindowKey();
			if (nextWindowKey !== windowKey) {
				setWindowKey(nextWindowKey);
			}
		}, 10_000);
		return () => clearInterval(timer);
	}, [mode, queryClient, windowKey]);

	// Reload the page when a new version is detected (written to localStorage by the start middleware)
	const initialVersion = useRef(isServer ? undefined : window.localStorage.getItem(APP_VERSION_STORAGE_KEY));
	useEffect(() => {
		const latestVersion = window.localStorage.getItem(APP_VERSION_STORAGE_KEY);
		if (!latestVersion || latestVersion === initialVersion.current) return;

		toast.info(`New version available: ${latestVersion.substring(0, 6)}`, {
			description: `Reloading…`,
			duration: 6_000,
		});
		const reloadTimeout = setTimeout(() => window.location.reload(), 8_000);

		return () => clearTimeout(reloadTimeout);
	}, [windowKey, mode]);

	const handleToggle = () => {
		startTransition(() => {
			void navigate({
				search: { mode: mode === 'day' ? 'realtime' : 'day' },
				replace: true,
			});
		});
	};

	return (
		<Layout className={PAGE_CLASS}>
			<div className={COUNTERS_STACK_CLASS}>
				<Suspense fallback={<CounterFallback />}>
					<Counters windowKey={windowKey} mode={mode} />
				</Suspense>
			</div>

			<ModeToggle mode={mode} onToggle={handleToggle} />
			<MysteriousFruit />
		</Layout>
	);
}

function CounterFallback() {
	return (
		<div className="text-primary text-center text-[clamp(3.5rem,11vw,12rem)] leading-none font-bold tracking-tight">
			<span className="opacity-40">...</span>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Live counter with donation feed
// Same mechanism as the landing page (AnimatedTotalRaised + real timestamps)
// ---------------------------------------------------------------------------

/**
 * Tracks which donations have "arrived" (their shifted timestamp has been
 * reached) and exposes them as a capped, growing feed. Shared by the global
 * and emergency counters. The `feedKey` keeps the two feeds' ids disjoint, and
 * resetting it (or changing mode) clears the feed.
 */
function useDonationFeed(donations: ApiDonation[], mode: LiveCounterMode, feedKey: string): FeedDonation[] {
	const arrivedIdsRef = useRef(new Set<string>());
	const [feedItems, setFeedItems] = useState<FeedDonation[]>([]);

	// Reset feed when the mode or feed identity changes
	const prevKeyRef = useRef(`${feedKey}:${mode}`);
	useEffect(() => {
		const key = `${feedKey}:${mode}`;
		if (prevKeyRef.current !== key) {
			prevKeyRef.current = key;
			arrivedIdsRef.current = new Set();
			setFeedItems([]);
		}
	}, [feedKey, mode]);

	// Poll for newly arrived donations at their real timestamps.
	// IDs are content-based (feedKey+mode+timestamp+amount) so that:
	// 1. placeholderData from a different mode doesn't block real data (different timestamps)
	// 2. Donations keep stable IDs across refetches even if array indices shift
	// We never clear the feed — old items stay and new-window donations append
	// naturally, just like the counter transitions seamlessly.
	useEffect(() => {
		const tick = () => {
			const now = Date.now();
			let hasNew = false;
			const newArrivals: FeedDonation[] = [];
			for (const d of donations) {
				const id = `${feedKey}-${mode}-${d.timestamp}-${d.amountCents}`;
				if (d.timestamp <= now && !arrivedIdsRef.current.has(id)) {
					arrivedIdsRef.current.add(id);
					newArrivals.push({ ...d, id });
					hasNew = true;
				}
			}
			if (hasNew) {
				setFeedItems((prev) => [...prev, ...newArrivals].slice(-20));
			}
		};

		tick();
		const timer = setInterval(tick, 50);
		return () => clearInterval(timer);
	}, [donations, mode, feedKey]);

	return feedItems;
}

// ---------------------------------------------------------------------------
// Counters — fetches both the global and emergency totals together so it can
// lay them out as a single unit. When an emergency is active, both counters
// shrink so the two fit on screen at once; the emergency one sits on top.
// ---------------------------------------------------------------------------

function Counters({ windowKey, mode }: { windowKey: string; mode: LiveCounterMode }) {
	const { language } = useTranslation();
	const { data: globalData } = useSuspenseQuery(
		trpc.portal.getRecentDonations.queryOptions({ windowKey, mode }, { staleTime: 10 * 60 * 1000 }),
	);
	const { data: emergencyData } = useSuspenseQuery(
		trpc.portal.getEmergencyDonations.queryOptions({ windowKey, mode }, { staleTime: 10 * 60 * 1000 }),
	);
	const hasEmergency = emergencyData != null;

	return (
		<>
			{hasEmergency && <EmergencyCounter data={emergencyData} mode={mode} language={language} />}
			<LiveCounter data={globalData} mode={mode} language={language} compact={hasEmergency} />
		</>
	);
}

function LiveCounter({
	data,
	mode,
	language,
	compact,
}: {
	data: { totalAtWindowStartCents: number; donations: ApiDonation[] };
	mode: LiveCounterMode;
	language: Locale;
	/** Shrink the counter so it shares the screen with the emergency counter. */
	compact: boolean;
}) {
	const deferredData = useDeferredValue(data);
	const feedItems = useDonationFeed(deferredData.donations, mode, 'global');

	return (
		<div className="relative w-full">
			{/* A little celebration shower on every donation that lands */}
			<CelebrationBursts feedItems={feedItems} />

			{/* Header — logo + tagline */}
			<div className="mb-6 flex flex-col items-center gap-3">
				<WorldcooLogo
					className={`${compact ? 'h-[clamp(2.25rem,4.5vw,5rem)]' : 'h-[clamp(3rem,6vw,8rem)]'} w-auto`}
					accentClassName="text-primary"
				/>
				<p className="text-[clamp(1rem,1.8vw,2.1rem)] tracking-wide">Donations collected</p>
			</div>

			{/* Counter — centered, stable position */}
			<div
				className={`text-primary text-center leading-none font-bold tracking-tight ${
					compact ? 'text-[clamp(2.5rem,7vw,6.5rem)]' : 'text-[clamp(4rem,13vw,14rem)]'
				}`}
			>
				<AnimatedTotalRaised
					totalAtWindowStartCents={deferredData.totalAtWindowStartCents}
					donations={deferredData.donations}
					language={language}
				/>
			</div>

			{/* Feed — absolutely positioned below the counter so it never shifts it */}
			<div className="absolute inset-x-0 top-full flex justify-center px-8">
				<DonationFeed donations={feedItems} variant={compact ? 'global-compact' : 'global'} />
			</div>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Emergency counter — sits above the global counter, styled in red. Only
// rendered by <Counters> when the server reports an active emergency. Its feed
// is kept compact and in normal flow so it never overlaps the global counter.
// ---------------------------------------------------------------------------

function EmergencyCounter({
	data,
	mode,
	language,
}: {
	data: { totalAtWindowStartCents: number; donations: ApiDonation[] };
	mode: LiveCounterMode;
	language: Locale;
}) {
	const deferredData = useDeferredValue(data);
	const feedItems = useDonationFeed(deferredData.donations, mode, 'emergency');

	return (
		<div className="flex w-full max-w-5xl flex-col items-center rounded-3xl border-2 border-red-200 bg-red-50/70 px-6 py-[clamp(1rem,2.5vh,2rem)]">
			{/* Header — pulsing badge */}
			<div className="mb-3 flex items-center gap-2.5 rounded-full bg-red-600 px-4 py-1.5 text-white shadow-sm">
				<span className="relative flex h-2 w-2">
					<span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-75" />
					<span className="relative inline-flex h-2 w-2 rounded-full bg-white" />
				</span>
				<span className="text-[clamp(0.75rem,1.2vw,1.3rem)] font-semibold tracking-wide uppercase">
					Active emergency
				</span>
			</div>

			<p className="mb-1 text-[clamp(0.9rem,1.5vw,1.8rem)] tracking-wide text-red-700">Emergency donations collected</p>

			{/* Counter — red */}
			<div className="text-center text-[clamp(2.25rem,6.5vw,6rem)] leading-none font-bold tracking-tight text-red-600">
				<AnimatedTotalRaised
					totalAtWindowStartCents={deferredData.totalAtWindowStartCents}
					donations={deferredData.donations}
					language={language}
				/>
			</div>

			{/* Compact feed in normal flow so it never overlaps the global counter */}
			<DonationFeed donations={feedItems} variant="emergency" />
		</div>
	);
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** "EROSKI68 - Ayuda a víctimas - 03/2020" → "Ayuda a víctimas" */
function parseCampaignName(name: string | null): string | null {
	if (!name) return null;
	const parts = name.split(' - ');
	if (parts.length <= 1) return name.trim();
	// If the last segment looks like a date (e.g. "03/2020", "(03/2020)"), use the previous one
	const last = parts.at(-1)!.trim();
	if (/^\(?\d{1,2}\/\d{4}\)?$/.test(last) && parts.length >= 3) {
		return parts.at(-2)!.trim();
	}
	return last;
}

// ---------------------------------------------------------------------------
// Donation feed — motion layout animations
// ---------------------------------------------------------------------------

type FeedVariant = 'global' | 'global-compact' | 'emergency';

const MAX_VISIBLE_BY_VARIANT: Record<FeedVariant, number> = {
	'global': 12,
	'global-compact': 6,
	'emergency': 2,
};

const sharedSpring = {
	type: 'spring' as const,
	stiffness: 320,
	damping: 30,
};

const enterTransition = {
	layout: sharedSpring,
	y: sharedSpring,
	opacity: { delay: 0.1, duration: 0.3, ease: 'easeOut' as const },
};

function DonationFeed({ donations, variant = 'global' }: { donations: FeedDonation[]; variant?: FeedVariant }) {
	const isEmergency = variant === 'emergency';

	// Newest-first for display. The emergency feed sits in normal flow, so it
	// only renders the visible rows — otherwise faded-out rows would still take
	// vertical space and stretch the card. The global feed is absolutely
	// positioned, so it can keep extra rows for the opacity fade-out effect.
	const allItems = [...donations]
		// oxlint-disable-next-line unicorn/no-array-reverse
		.reverse()
		.slice(0, isEmergency ? MAX_VISIBLE_BY_VARIANT[variant] : undefined);

	return (
		<div className="mt-4 w-full">
			{/* Waiting state — hidden for the emergency feed to keep it compact */}
			{!isEmergency && (
				<AnimatePresence>
					{allItems.length === 0 && (
						<motion.div
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
							transition={{ duration: 0.3 }}
							className="flex items-center justify-center pt-5 text-[clamp(1rem,1.3vw,1.55rem)] text-gray-300"
						>
							<span className="flex items-center gap-2">
								<span className="relative flex h-1.5 w-1.5">
									<span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-gray-300 opacity-75" />
									<span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-gray-300" />
								</span>
								Listening for donations...
							</span>
						</motion.div>
					)}
				</AnimatePresence>
			)}

			{/* Donation rows */}
			<div className="flex flex-col items-center">
				<AnimatePresence initial={false}>
					{allItems.map((d, i) => (
						<DonationRow key={d.id} donation={d} index={i} variant={variant} />
					))}
				</AnimatePresence>
			</div>
		</div>
	);
}

function DonationRow({
	donation,
	index,
	variant = 'global',
}: {
	donation: FeedDonation;
	index: number;
	variant?: FeedVariant;
}) {
	const flagUrl = donation.country ? getCountryFlagUrl(donation.country) : undefined;
	const amount = (donation.amountCents / 100).toFixed(2);
	const campaignLabel = parseCampaignName(donation.campaignName);
	const hasLocation = flagUrl || donation.city;
	const isEmergency = variant === 'emergency';

	const maxVisible = MAX_VISIBLE_BY_VARIANT[variant];
	const targetOpacity = index >= maxVisible ? 0 : index === 0 ? 1 : Math.max(0.25, 1 - index * 0.1);

	// The emergency feed is more compact, so amounts render a touch smaller and in red.
	const amountClassName = isEmergency
		? 'shrink-0 text-[clamp(1.4rem,2.3vw,2.7rem)] font-semibold tabular-nums text-red-600'
		: 'text-primary shrink-0 text-[clamp(1.75rem,2.9vw,3.5rem)] font-semibold tabular-nums';

	return (
		<motion.div
			layout
			initial={{ opacity: 0, y: -30 }}
			animate={{ opacity: targetOpacity, y: 0 }}
			transition={enterTransition}
			className="w-full"
		>
			<div className="flex flex-col items-center justify-center px-2 py-2">
				<div className="flex flex-wrap items-center justify-center gap-2">
					<span className={amountClassName}>+{amount}€</span>
					{campaignLabel && (
						<>
							<span className="shrink-0 text-[clamp(1.35rem,2.1vw,2.5rem)] text-gray-400">para</span>
							<span className="text-[clamp(1.35rem,2.1vw,2.5rem)] font-medium text-gray-700">{campaignLabel}</span>
						</>
					)}
				</div>

				<div className="flex flex-wrap items-center justify-center gap-2">
					{donation.merchantName && (
						<span className="text-[clamp(1.35rem,2.1vw,2.5rem)] text-gray-400">
							en {donation.merchantName}
							{hasLocation && ', '}
						</span>
					)}
					{hasLocation && (
						<span className="flex items-center gap-1.5 text-[clamp(1.35rem,2.1vw,2.5rem)] text-gray-400">
							{donation.city}
							{flagUrl && (
								<img src={flagUrl} alt="" className="h-[clamp(1.1rem,1.7vw,2rem)] w-[clamp(1.1rem,1.7vw,2rem)]" />
							)}
						</span>
					)}
				</div>
			</div>
		</motion.div>
	);
}

// ---------------------------------------------------------------------------
// Mode toggle — switch between day offset and realtime
// ---------------------------------------------------------------------------

function ModeToggle({ mode, onToggle }: { mode: LiveCounterMode; onToggle: () => void }) {
	const isRealtime = mode === 'realtime';

	return (
		<div className="fixed right-4 bottom-4 flex items-center gap-2.5 rounded-full bg-white/80 px-3 py-2 shadow-md backdrop-blur-sm">
			<span className={`text-xs font-medium ${!isRealtime ? 'text-gray-900' : 'text-gray-400'}`}>-1 day</span>
			<button
				type="button"
				role="switch"
				aria-checked={isRealtime}
				aria-label={`Switch to ${isRealtime ? 'day offset' : 'realtime'} mode`}
				onClick={onToggle}
				className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full transition-colors duration-200 ${
					isRealtime ? 'bg-primary' : 'bg-gray-300'
				}`}
			>
				<span
					className={`pointer-events-none inline-block h-4 w-4 translate-y-0.5 rounded-full bg-white shadow-sm ring-0 transition-transform duration-200 ${
						isRealtime ? 'translate-x-[18px]' : 'translate-x-0.5'
					}`}
				/>
			</button>
			<span className={`text-xs font-medium ${isRealtime ? 'text-gray-900' : 'text-gray-400'}`}>Realtime</span>
		</div>
	);
}

// ---------------------------------------------------------------------------
// The mysterious fruit
// Something is growing, quietly, in the corner. No one knows quite what it is.
// Each week it takes the shape of a different fruit, and it will keep changing
// until the harvest day arrives. Hover over it, if you dare, to learn its name.
// ---------------------------------------------------------------------------

const HARVEST_DAY = new Date('2026-11-30T00:00:00').getTime();
const MOONS_UNTIL_RIPE = 40;
const ONE_MOON = 7 * 24 * 60 * 60 * 1000;

/** The shapes the mysterious fruit takes as it ripens, moon by moon. */
const FORMS_OF_THE_FRUIT = [
	{ moon: 4, emoji: '🌱', name: 'una semilla' },
	{ moon: 6, emoji: '🫛', name: 'un guisante' },
	{ moon: 7, emoji: '🫐', name: 'un arándano' },
	{ moon: 9, emoji: '🍒', name: 'una cereza' },
	{ moon: 10, emoji: '🍓', name: 'una fresa' },
	{ moon: 12, emoji: '🍋', name: 'un limón' },
	{ moon: 14, emoji: '🍑', name: 'un melocotón' },
	{ moon: 15, emoji: '🍎', name: 'una manzana' },
	{ moon: 16, emoji: '🥑', name: 'un aguacate' },
	{ moon: 17, emoji: '🍐', name: 'una pera' },
	{ moon: 18, emoji: '🫑', name: 'un pimiento' },
	{ moon: 19, emoji: '🥭', name: 'un mango' },
	{ moon: 20, emoji: '🍌', name: 'un plátano' },
	{ moon: 21, emoji: '🥕', name: 'una zanahoria' },
	{ moon: 24, emoji: '🌽', name: 'una mazorca de maíz' },
	{ moon: 26, emoji: '🥬', name: 'una lechuga' },
	{ moon: 28, emoji: '🍆', name: 'una berenjena' },
	{ moon: 30, emoji: '🥥', name: 'un coco' },
	{ moon: 33, emoji: '🍍', name: 'una piña' },
	{ moon: 35, emoji: '🍈', name: 'un melón' },
	{ moon: 37, emoji: '🥦', name: 'una coliflor' },
	{ moon: 39, emoji: '🎃', name: 'una calabaza' },
	{ moon: 40, emoji: '🍉', name: 'una sandía' },
] as const;

function divineTheFruit(now: number) {
	// When the harvest day comes, the fruit is a fruit no longer...
	if (now >= HARVEST_DAY) {
		return { moon: MOONS_UNTIL_RIPE, emoji: '👶', name: '¡ya está aquí!' };
	}
	const moon = Math.max(4, Math.round(MOONS_UNTIL_RIPE - (HARVEST_DAY - now) / ONE_MOON));
	let form: (typeof FORMS_OF_THE_FRUIT)[number] = FORMS_OF_THE_FRUIT[0];
	for (const candidate of FORMS_OF_THE_FRUIT) {
		if (moon >= candidate.moon) form = candidate;
	}
	return { moon, emoji: form.emoji, name: form.name };
}

function MysteriousFruit() {
	const { name, emoji } = divineTheFruit(Date.now());

	// Poke the fruit and it lets loose a shower too. Each press adds a burst,
	// keyed by a monotonic counter so repeated pokes can overlap.
	const [bursts, setBursts] = useState<number[]>([]);
	const nextId = useRef(0);

	const poke = () => {
		const id = nextId.current++;
		setBursts((prev) => [...prev, id]);
	};

	return (
		<>
			<HueWash active={bursts.length > 0} />
			<div className="pointer-events-none fixed inset-0 z-50 overflow-hidden">
				<AnimatePresence>
					{bursts.map((id) => (
						<ConfettiBurst key={id} onDone={() => setBursts((prev) => prev.filter((b) => b !== id))} />
					))}
				</AnimatePresence>
			</div>

			<button
				type="button"
				onClick={poke}
				aria-label="Poke the fruit"
				className="group fixed bottom-4 left-4 flex cursor-pointer items-center gap-2 select-none"
			>
				<motion.span
					className="text-[clamp(1.35rem,2.1vw,2.5rem)] opacity-70 transition-opacity duration-300 group-hover:opacity-100"
					// A brief shiver, then stillness for 30 seconds — as if it stirred.
					animate={{ rotate: [0, -10, 9, -5, 0], y: [0, -3, 0, -2, 0] }}
					transition={{
						duration: 1.2,
						times: [0, 0.2, 0.45, 0.7, 1],
						repeat: Infinity,
						repeatDelay: 30,
						ease: 'easeInOut',
					}}
				>
					{emoji}
				</motion.span>
				<span className="max-w-0 overflow-hidden text-[clamp(1.75rem,2.9vw,3.5rem)] whitespace-nowrap text-gray-400 opacity-0 transition-all duration-300 group-hover:max-w-[18rem] group-hover:opacity-100">
					{name}
				</span>
			</button>
		</>
	);
}

// ---------------------------------------------------------------------------
// Celebration shower
// A festive effect that rains down whenever a donation lands (and on a poke of
// the fruit). Two colour schemes ship side by side; which one actually plays is
// resolved at runtime from the seed below, so the source itself gives nothing
// away until the day.
// ---------------------------------------------------------------------------

// Both entries are kept verbatim so the styler compiles each class; the glyphs
// are stored as code points rather than literals. Only the seed picks a scheme.
const SCHEMES = [
	{ wash: 'bg-pink-400/20', glyphs: [0x1f380, 0x1f497, 0x1f467, 0x1f338, 0x1f496, 0x1f984] },
	{ wash: 'bg-blue-400/20', glyphs: [0x1f499, 0x1f466, 0x1fa75, 0x2b50, 0x1f682, 0x1f433] },
] as const;

// Same LCG constants as the shower spread, reused as a tiny fixed seed; its low
// bit selects the live scheme.
const SCHEME_SEED = (0x2a * 9301 + 49297) % 233280;
const SCHEME = SCHEMES[SCHEME_SEED & 1]!;
const SCHEME_GLYPHS = SCHEME.glyphs.map((cp) => String.fromCodePoint(cp));

/** A soft full-screen colour wash, shown while any shower is active. */
function HueWash({ active }: { active: boolean }) {
	return (
		<AnimatePresence>
			{active && (
				<motion.div
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					exit={{ opacity: 0 }}
					transition={{ duration: 0.5 }}
					className={`pointer-events-none fixed inset-0 z-40 ${SCHEME.wash}`}
				/>
			)}
		</AnimatePresence>
	);
}

/**
 * Spawns a fresh shower for every donation that arrives in the feed. Bursts are
 * keyed by the donation id so several can overlap and each cleans itself up
 * when it finishes falling.
 */
function CelebrationBursts({ feedItems }: { feedItems: FeedDonation[] }) {
	const seenIds = useRef(new Set<string>());
	const [bursts, setBursts] = useState<string[]>([]);

	useEffect(() => {
		const fresh = feedItems.filter((d) => !seenIds.current.has(d.id));
		if (fresh.length === 0) return;
		fresh.forEach((d) => seenIds.current.add(d.id));
		setBursts((prev) => [...prev, ...fresh.map((d) => d.id)]);
	}, [feedItems]);

	const handleDone = (id: string) => setBursts((prev) => prev.filter((b) => b !== id));

	return (
		<>
			<HueWash active={bursts.length > 0} />
			<div className="pointer-events-none fixed inset-0 z-50 overflow-hidden">
				<AnimatePresence>
					{bursts.map((id) => (
						<ConfettiBurst key={id} onDone={() => handleDone(id)} />
					))}
				</AnimatePresence>
			</div>
		</>
	);
}

/** A single short-lived shower of celebratory glyphs raining down the screen. */
function ConfettiBurst({ onDone }: { onDone: () => void }) {
	const glyphs = SCHEME_GLYPHS;

	// Pseudo-random spread so the shower looks natural. Computed once per burst.
	const pieces = useMemo(
		() =>
			Array.from({ length: 28 }, (_, i) => {
				const seed = (i * 9301 + 49297) % 233280;
				const rand = seed / 233280;
				return {
					id: i,
					emoji: glyphs[i % glyphs.length],
					left: (i * 37) % 100,
					delay: rand * 0.5,
					duration: 1.8 + rand * 1.4,
					drift: (rand - 0.5) * 140,
					spin: (rand - 0.5) * 640,
					size: 1.2 + rand * 1.8,
				};
			}),
		[glyphs],
	);

	useEffect(() => {
		const timer = setTimeout(onDone, 3500);
		return () => clearTimeout(timer);
	}, [onDone]);

	return (
		<>
			{pieces.map((p) => (
				<motion.span
					key={p.id}
					className="absolute top-0"
					style={{ left: `${p.left}%`, fontSize: `${p.size}rem` }}
					initial={{ y: '-10vh', x: 0, rotate: 0, opacity: 0 }}
					animate={{ y: '110vh', x: p.drift, rotate: p.spin, opacity: [0, 1, 1, 0.9] }}
					exit={{ opacity: 0 }}
					transition={{ duration: p.duration, delay: p.delay, ease: 'easeIn' }}
				>
					{p.emoji}
				</motion.span>
			))}
		</>
	);
}
